import {Box, Switch, FormControlLabel, Typography} from '@mui/material'
import {noop} from 'lodash'
import moment, {Moment} from 'moment-timezone'
import React, {useState} from 'react'

import {Dates} from '../../../common'

import {
  localDateTimeRangeToTimeRangeParam,
  selectionItemInLocalTime,
  isSameDay,
  sanitizeLocalDateRange
} from './dateRangePickerHelpers'
import {datePickerOnBlueSx, SimpleDatePicker, DatePickerOptions} from './SimpleDatePicker'
import {QuickSelectionItem, SelectionItem} from './types'

type DateRangePickerProps<T extends string> = {
  timezone: string // time zone of the start and end of days
  singleDateSwitchLabel?: string
  timeRangeParam?: [Moment | null, Moment | null]
  onTimeRangeParamChange?: (timeRange: string | undefined) => void
  selectionItems?: SelectionItem<T>[]
  selectedItem?: QuickSelectionItem
  fullWidth?: boolean
  label?: string
  errorText?: string
  disabled?: boolean
  isSingleDateDefault?: boolean
  isContrastMode?: boolean
  dataTestId?: string
  useEndOfDayForSingleDate?: boolean
  disableDateSwitch?: boolean
  withForm?: boolean
  hideSingleDateSwitch?: boolean
  isOutsideRange?: (arg: Moment, selectedStartDate?: Moment) => boolean
} & DatePickerOptions

export const DateRangePicker = <T extends string>({
  timezone,
  timeRangeParam: timeRangeLocalTime = [null, null],
  onTimeRangeParamChange: setTimeRange = noop,
  singleDateSwitchLabel,
  selectionItems = [],
  selectedItem,
  fullWidth,
  label,
  errorText,
  isSingleDateDefault,
  disableDateSwitch,
  isContrastMode = true,
  useEndOfDayForSingleDate = true,
  dataTestId,
  hideSingleDateSwitch,
  ...rest
}: DateRangePickerProps<T>): React.ReactElement => {
  const [singleDate, setSingleDate] = useState(isSingleDateDefault || isSameDay(timeRangeLocalTime))

  const calendarWrapper: (innerPicker: React.ReactNode) => React.ReactNode = (innerPicker) => {
    return (
      <Box display={'flex'} flexDirection={'column'}>
        <Box flex={1}>{innerPicker}</Box>
        <FormControlLabel
          control={
            <Switch
              disabled={disableDateSwitch}
              data-test-id="date-range-picker-single-date-switch"
              checked={singleDate}
              onChange={(_, checked) => setSingleDate(checked)}
            />
          }
          label={<Typography variant={'caption'}>{singleDateSwitchLabel}</Typography>}
        />
      </Box>
    )
  }

  const handleRangeChange = (range: Dates, item: QuickSelectionItem | undefined) => {
    const localTimeRange = sanitizeLocalDateRange(range)
    setSingleDate(isSameDay(localTimeRange))
    setTimeRange(item ? item.id : localDateTimeRangeToTimeRangeParam(localTimeRange, timezone))
  }

  return (
    <SimpleDatePicker
      dataTestId={dataTestId}
      label={label}
      errorText={errorText}
      calendarWrapper={hideSingleDateSwitch ? undefined : calendarWrapper}
      sx={isContrastMode ? datePickerOnBlueSx : {}}
      dateRange={singleDate ? undefined : timeRangeLocalTime}
      date={singleDate ? timeRangeLocalTime[0] : undefined}
      handleRangeChange={handleRangeChange}
      handleDateChange={(date) => {
        setTimeRange(
          localDateTimeRangeToTimeRangeParam(
            [date, useEndOfDayForSingleDate ? moment.tz(date, timezone).endOf('day') : date],
            timezone
          )
        )
      }}
      selectedQuickSelectItem={
        selectedItem ? selectionItemInLocalTime(selectedItem, timezone) : undefined
      }
      quickSelectionItems={selectionItems.map((item) => selectionItemInLocalTime(item, timezone))}
      fullWidth={fullWidth}
      timezone={timezone}
      {...rest}
    />
  )
}
